import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, 
  Mail, 
  Plus, 
  ChevronLeft, 
  ChevronRight,
  Filter,
  RefreshCw,
  Clock,
  Send,
  FileText,
  Copy,
  BarChart2,
  X,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, formatP0Error } from '../lib/api';

const EMAIL_TYPES = [
  { value: 'welcome', label: 'Welcome', color: 'bg-green-100 text-green-800' },
  { value: 'newsletter', label: 'Newsletter', color: 'bg-blue-100 text-blue-800' },
  { value: 'nurture', label: 'Nurture', color: 'bg-purple-100 text-purple-800' },
  { value: 'growth', label: 'Growth', color: 'bg-orange-100 text-orange-800' },
];

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  scheduled: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

export default function EmailGrowthCalendar() {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  // State
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month | list
  const [filters, setFilters] = useState({ status: '', email_type: '', project_id: '' });
  const [projects, setProjects] = useState([]);
  
  // Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Form state
  const [newEvent, setNewEvent] = useState({
    title: '',
    email_type: 'newsletter',
    scheduled_date: new Date().toISOString().split('T')[0],
    content: '',
    subject: '',
    preheader: ''
  });
  
  // Fetch events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.email_type) params.email_type = filters.email_type;
      if (filters.project_id) params.project_id = filters.project_id;
      
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.get('/email/calendar', { params });
      
      if (response.data.ok) {
        setEvents(response.data.events || []);
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error(formatP0Error(error) || 'Erreur lors du chargement du calendrier');
    } finally {
      setLoading(false);
    }
  }, [filters]);
  
  // Fetch projects
  const fetchProjects = useCallback(async () => {
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.get('/projects');
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, []);
  
  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.get('/email/stats');
      if (response.data.ok) {
        setStats(response.data.total_stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);
  
  useEffect(() => {
    fetchEvents();
    fetchProjects();
    fetchStats();
  }, [fetchEvents, fetchProjects, fetchStats]);
  
  // Create event
  const handleCreateEvent = async () => {
    if (!newEvent.title || !filters.project_id) {
      toast.error('Veuillez remplir le titre et sélectionner un projet');
      return;
    }
    
    setCreating(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/email/calendar', {
        ...newEvent,
        project_id: filters.project_id,
        scheduled_date: new Date(newEvent.scheduled_date).toISOString()
      });
      
      if (response.data.ok) {
        toast.success('Événement créé !');
        setCreateModalOpen(false);
        setNewEvent({
          title: '',
          email_type: 'newsletter',
          scheduled_date: new Date().toISOString().split('T')[0],
          content: '',
          subject: '',
          preheader: ''
        });
        fetchEvents();
      }
    } catch (error) {
      console.error('Create error:', error);
      toast.error(formatP0Error(error) || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };
  
  // Update event status
  const handleUpdateStatus = async (eventId, newStatus) => {
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.patch(`/email/calendar/${eventId}`, { status: newStatus });
      
      if (response.data.ok) {
        toast.success(`Statut mis à jour: ${newStatus}`);
        fetchEvents();
        setDetailModalOpen(false);
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error(formatP0Error(error) || 'Erreur lors de la mise à jour');
    }
  };
  
  // Duplicate event
  const handleDuplicate = async (event) => {
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/email/calendar', {
        project_id: event.project_id,
        title: `${event.title} (copie)`,
        email_type: event.email_type,
        scheduled_date: new Date().toISOString(),
        content: event.text,
        subject: event.subject,
        preheader: event.preheader
      });
      
      if (response.data.ok) {
        toast.success('Événement dupliqué !');
        fetchEvents();
        setDetailModalOpen(false);
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la duplication');
    }
  };
  
  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    for (let i = 0; i < startingDay; i++) {
      const prevDate = new Date(year, month, -startingDay + i + 1);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Next month days
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };
  
  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => e.scheduled_date?.split('T')[0] === dateStr);
  };
  
  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  
  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedDate(newDate);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Retour
            </Button>
            <div className="flex items-center gap-2">
              <Mail className="w-6 h-6 text-blue-600" />
              <h1 className="text-xl font-bold">Email Growth Calendar</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchEvents} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" />
              Rafraîchir
            </Button>
            <Button onClick={() => setCreateModalOpen(true)} data-testid="create-event-btn">
              <Plus className="w-4 h-4 mr-1" />
              Nouvel événement
            </Button>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card data-testid="stat-total">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total || 0}</p>
                <p className="text-sm text-gray-500">Total emails</p>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="stat-draft">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FileText className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.by_status?.draft || 0}</p>
                <p className="text-sm text-gray-500">Brouillons</p>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="stat-scheduled">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.by_status?.scheduled || 0}</p>
                <p className="text-sm text-gray-500">Planifiés</p>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="stat-sent">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Send className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.by_status?.sent || 0}</p>
                <p className="text-sm text-gray-500">Envoyés</p>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Filters */}
        <Card className="mb-6" data-testid="filters-card">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium">Filtres:</span>
              </div>
              
              <Select value={filters.project_id || "all"} onValueChange={(v) => setFilters({...filters, project_id: v === "all" ? "" : v})}>
                <SelectTrigger className="w-[180px]" data-testid="filter-project">
                  <SelectValue placeholder="Tous les projets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les projets</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filters.status || "all"} onValueChange={(v) => setFilters({...filters, status: v === "all" ? "" : v})}>
                <SelectTrigger className="w-[150px]" data-testid="filter-status">
                  <SelectValue placeholder="Tous statuts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  <SelectItem value="draft">Brouillon</SelectItem>
                  <SelectItem value="scheduled">Planifié</SelectItem>
                  <SelectItem value="sent">Envoyé</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filters.email_type || "all"} onValueChange={(v) => setFilters({...filters, email_type: v === "all" ? "" : v})}>
                <SelectTrigger className="w-[150px]" data-testid="filter-type">
                  <SelectValue placeholder="Tous types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous types</SelectItem>
                  {EMAIL_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex gap-1 ml-auto">
                <Button 
                  variant={viewMode === 'month' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setViewMode('month')}
                >
                  <CalendarIcon className="w-4 h-4" />
                </Button>
                <Button 
                  variant={viewMode === 'list' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <BarChart2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Calendar View */}
        {loading ? (
          <Card className="p-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </Card>
        ) : viewMode === 'month' ? (
          <Card data-testid="calendar-month-view">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <Button variant="ghost" onClick={() => navigateMonth(-1)}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <CardTitle className="text-lg">
                {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}
              </CardTitle>
              <Button variant="ghost" onClick={() => navigateMonth(1)}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent>
              {/* Days header */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {getDaysInMonth(selectedDate).map((day, idx) => {
                  const dayEvents = getEventsForDate(day.date);
                  const isToday = day.date.toDateString() === new Date().toDateString();
                  
                  return (
                    <div 
                      key={idx}
                      className={`min-h-[80px] p-1 border rounded-lg ${
                        day.isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                      } ${isToday ? 'border-blue-500 border-2' : 'border-gray-200'}`}
                    >
                      <div className={`text-sm font-medium mb-1 ${
                        day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                      }`}>
                        {day.date.getDate()}
                      </div>
                      <div className="space-y-1">
                        {dayEvents.slice(0, 2).map(event => (
                          <div 
                            key={event.id}
                            onClick={() => { setSelectedEvent(event); setDetailModalOpen(true); }}
                            className={`text-xs p-1 rounded truncate cursor-pointer hover:opacity-80 ${
                              EMAIL_TYPES.find(t => t.value === event.email_type)?.color || 'bg-gray-100'
                            }`}
                            data-testid={`event-${event.id}`}
                          >
                            {event.title}
                          </div>
                        ))}
                        {dayEvents.length > 2 && (
                          <div className="text-xs text-gray-500 pl-1">
                            +{dayEvents.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : (
          /* List View */
          <Card data-testid="calendar-list-view">
            <CardHeader>
              <CardTitle>Tous les événements ({events.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun événement email</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setCreateModalOpen(true)}
                  >
                    Créer le premier événement
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {events.map(event => (
                    <div 
                      key={event.id}
                      onClick={() => { setSelectedEvent(event); setDetailModalOpen(true); }}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                      data-testid={`list-event-${event.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Badge className={EMAIL_TYPES.find(t => t.value === event.email_type)?.color}>
                          {event.email_type}
                        </Badge>
                        <div>
                          <p className="font-medium">{event.title}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(event.scheduled_date).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <Badge className={STATUS_COLORS[event.validation_flag] || STATUS_COLORS.draft}>
                        {event.validation_flag || 'draft'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
      
      {/* Create Event Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg" data-testid="create-modal">
          <DialogHeader>
            <DialogTitle>Nouvel événement email</DialogTitle>
            <DialogDescription>
              Créez un nouvel email dans votre calendrier de croissance.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!filters.project_id && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                ⚠️ Sélectionnez d'abord un projet dans les filtres
              </div>
            )}
            
            <div>
              <Label htmlFor="title">Titre *</Label>
              <Input 
                id="title"
                value={newEvent.title}
                onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                placeholder="Newsletter de janvier"
                data-testid="input-title"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email_type">Type</Label>
                <Select 
                  value={newEvent.email_type} 
                  onValueChange={(v) => setNewEvent({...newEvent, email_type: v})}
                >
                  <SelectTrigger data-testid="select-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMAIL_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="date">Date</Label>
                <Input 
                  id="date"
                  type="date"
                  value={newEvent.scheduled_date}
                  onChange={(e) => setNewEvent({...newEvent, scheduled_date: e.target.value})}
                  data-testid="input-date"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="subject">Objet de l'email</Label>
              <Input 
                id="subject"
                value={newEvent.subject}
                onChange={(e) => setNewEvent({...newEvent, subject: e.target.value})}
                placeholder="🎉 Découvrez nos nouveautés"
                data-testid="input-subject"
              />
            </div>
            
            <div>
              <Label htmlFor="preheader">Preheader</Label>
              <Input 
                id="preheader"
                value={newEvent.preheader}
                onChange={(e) => setNewEvent({...newEvent, preheader: e.target.value})}
                placeholder="Aperçu du contenu..."
                data-testid="input-preheader"
              />
            </div>
            
            <div>
              <Label htmlFor="content">Contenu</Label>
              <Textarea 
                id="content"
                value={newEvent.content}
                onChange={(e) => setNewEvent({...newEvent, content: e.target.value})}
                placeholder="Corps de l'email..."
                rows={4}
                data-testid="input-content"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateEvent} disabled={creating || !filters.project_id} data-testid="submit-create">
              {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Créer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Event Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg" data-testid="detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent?.title}
              <Badge className={EMAIL_TYPES.find(t => t.value === selectedEvent?.email_type)?.color}>
                {selectedEvent?.email_type}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-gray-500" />
                <span>{new Date(selectedEvent.scheduled_date).toLocaleDateString('fr-FR', { 
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                })}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Badge className={STATUS_COLORS[selectedEvent.validation_flag] || STATUS_COLORS.draft}>
                  {selectedEvent.validation_flag || 'draft'}
                </Badge>
              </div>
              
              {selectedEvent.subject && (
                <div>
                  <Label className="text-gray-500">Objet</Label>
                  <p className="font-medium">{selectedEvent.subject}</p>
                </div>
              )}
              
              {selectedEvent.preheader && (
                <div>
                  <Label className="text-gray-500">Preheader</Label>
                  <p className="text-sm">{selectedEvent.preheader}</p>
                </div>
              )}
              
              {selectedEvent.text && (
                <div>
                  <Label className="text-gray-500">Contenu</Label>
                  <div className="bg-gray-50 rounded p-3 text-sm max-h-40 overflow-y-auto">
                    {selectedEvent.text}
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {selectedEvent.validation_flag !== 'sent' && (
                  <>
                    <Button 
                      size="sm"
                      onClick={() => handleUpdateStatus(selectedEvent.id, 'scheduled')}
                      disabled={selectedEvent.validation_flag === 'scheduled'}
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      Planifier
                    </Button>
                    <Button 
                      size="sm"
                      variant="outline"
                      className="text-green-600"
                      onClick={() => handleUpdateStatus(selectedEvent.id, 'sent')}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Marquer envoyé
                    </Button>
                  </>
                )}
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={() => handleDuplicate(selectedEvent)}
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Dupliquer
                </Button>
              </div>
              
              {/* Debug info (hidden by default) */}
              <details className="text-xs text-gray-400">
                <summary className="cursor-pointer">Debug info</summary>
                <pre className="bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                  {JSON.stringify({ id: selectedEvent.id, request_id: selectedEvent._request_id }, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
